<template>
  <div class="start-for-free-page-v2" :class="[$config.platform]">
    <StartLearningToday class="start-learn-today v2-head" class-name="start-your-journey-for-free-v2" event-prefix="start_your_journey_v2_hero_">
      <h1 class="heading">
        <span class="first">
          Get <span class="mobile-block">JOB-READY</span>
        </span>
        <span class="second">
          Learn <span class="highlight yellow shadow">{{ $config.platform === 'ds' ? 'DATA & AI' : 'FINANCE' }}</span><br class="laptop-only"> <span>for <span class="highlight blue shadow">FREE</span></span>
        </span>
      </h1>
      <ul>
        <li>First 2 course sections free</li>
        <li v-if="$config.platform === 'ds'">
          Learn SQL, Python, R, Excel, ChatGPT, ML, AI, etc.
        </li>
        <li v-else>
          Learn Excel, financial analysis, accounting, financial modeling, portfolio management, corporate finance, and more.
        </li>
        <li>Practice your skills with hands-on exercises.</li>
        <li v-if="$config.platform === 'ds'">
          Build a portfolio with real-world data projects.
        </li>
        <li v-else>
          Gain job-equivalent experience with real finance projects.
        </li>
      </ul>
      <div class="footer">
        <IconCreditCard />
        <span> No credit card required </span>
      </div>
    </StartLearningToday>

    <div class="trustpilot-section">
      <div class="container">
        <div class="row">
          <div class="col col-10 offset-1">
            <div class="trustpilot-review">
              <IconTrustpilot class="icon" />
              <Rating :reviews="{ rating: 4.9 }" />
            </div>
            <Companies
              v-if="companiesSmall && companiesSmall.length"
              :companies="companiesSmall" title-on-top title="Trusted by learners at top-tier companies:"
              no-fill no-container
            />
          </div>
        </div>
      </div>
    </div>

    <div class="section courses-extended">
      <div class="head container">
        <h2 class="section-title">
          Prepare for Your <span class="highlight yellow thin">Ideal Job</span> in {{ $config.platform === 'ds' ? 'Data & AI' : 'Finance' }}
        </h2>
        <h3 class="muted">
          What’s your ideal job?
        </h3>
        <ul>
          <li
            v-for="(g, idx) in coursesGroups" :key="idx" :class="{ active: idx === courseGroupIdx }"
            @click="switchCourseGroup(idx)"
          >
            {{ g.name }}
          </li>
        </ul>
        <p class="description">
          Become a certified
          <span class="highlight blue thin">{{ courseGroup.description.title }}<span class="mobile-only" /></span><span class="desktop-only">,</span> earning
          <span class="static-salary-container">
            <span class="animation-salary" :class="{ animated: salaryAnimation.animatedOnScroll }">
              <span
                class="holder animation" :class="{ active: salaryAnimation.activate }"
                :style="`--width: ${salaryAnimation.width || 188}px`"
                v-html="salary"
              />
            </span>
          </span>
        </p>
      </div>
      <Courses
        v-if="courses && courses.length" :courses="courses" :offset="-2"
        heading="h3"
      />
      <div class="btn-holder">
        <button class="btn-primary btn-lg" data-event="start_your_journey_v2_courses.click" @click="scrollToLastSection">
          Start for Free
        </button>
      </div>
    </div>

    <div class="info-section-wrapper">
      <div class="container info-section">
        <div class="first">
          <IconUsers />
          <p>No Time Limit</p>
          <p>Lifetime access to free features</p>
        </div>
        <div class="second">
          <IconThunder />
          <p>Suitable for All Levels</p>
          <p>Beginner to advanced courses</p>
        </div>
        <div class="third">
          <IconMonitor />
          <p>Flexible Schedule</p>
          <p>Learn anytime, anywhere, at your own pace</p>
        </div>
        <div class="fourth">
          <IconLayers />
          <p>Practice-Based Learning</p>
          <p>Hands-on exercises & real {{ $config.platform === 'ds' ? 'data' : 'finance' }} projects</p>
        </div>
      </div>
    </div>

    <div v-if="$config.platform === 'ds'" class="section graduations">
      <Videos :videos="videos" :video-js-loaded="videoJsLoaded" title-class="section-title">
        <template slot="title">
          Join <span class="highlight yellow thin">3 Million</span> Learners Online
        </template>
      </Videos>
      <div class="btn-holder">
        <button
          class="btn-primary btn-lg"
          data-event="start_your_journey_v2_testimonials.click"
          @click="scrollToLastSection"
        >
          Enroll Now
        </button>
      </div>
    </div>

    <Technologies v-if="$config.platform === 'ds' && technologies && technologies.length" :technologies="technologies" />

    <div v-if="platformInstructors && platformInstructors.length" class="section instructors">
      <div class="instructors-slider">
        <div class="container heading">
          <h2 class="section-title" v-html="instructorsTitle" />
          <Companies
            v-if="$config.platform === 'fa' && companiesFa && companiesFa.length" :companies="companiesFa"
            no-fill title=""
          />
        </div>
        <SliderInfinite :offset="0">
          <div
            v-for="instructor in platformInstructors"
            :key="instructor.slug"
            slot="slide"
          >
            <component :is="$config.platform === 'ds' ? 'a' : 'span'" :href="`/instructors/${instructor.slug}`" target="_blank">
              <img
                :src="require(`../../assets/images/landing/${$config.platform === 'fa' ? 'fa-' : ''}lp-sl-m-${instructor.image || instructor.slug}.webp`)"
                :srcset="`${require(`../../assets/images/landing/${$config.platform === 'fa' ? 'fa-' : ''}lp-sl-m-${instructor.image || instructor.slug}.webp`)} 1920w,
                  ${require(`../../assets/images/landing/${$config.platform === 'fa' ? 'fa-' : ''}lp-sl-m-${instructor.image || instructor.slug}-mobile.webp`)} 1024w`"
                :sizes="`(max-width: 1024px) 1024px,
                  ${require(`../../assets/images/landing/${$config.platform === 'fa' ? 'fa-' : ''}lp-sl-m-${instructor.image || instructor.slug}.webp`)}px`"
                :alt="instructor.name"
              >
            </component>
          </div>
        </SliderInfinite>
        <StatsIcons
          v-if="$config.platform === 'fa'"
          class="container"
          :stats="stats" colored auto-width
          :order="['students', 'courses', 'projects']"
        />
      </div>
    </div>

    <div v-if="$config.platform === 'fa'" class="section master">
      <div class="container">
        <div class="row">
          <div class="col col-4">
            <h3 class="section-title">
              Lack Training?<br>Gain <span class="highlight yellow thin">Job-Equivalent Experience</span>
            </h3>
            <p class="muted">
              Bridge the gap between learning and doing. Work on <span class="highlight thin white">real finance projects</span> you’ll encounter on the job while practicing your skills in the comfort of your home.
            </p>
            <div class="btn-holder desktop-only">
              <button class="btn-primary btn-lg" @click="scrollToLastSection">
                Start for Free
              </button>
            </div>
          </div>
          <div class="col col-8">
            <img :src="require(`../../assets/images/${$config.platform}-signup-master-v2.webp`)">
          </div>
        </div>
        <div class="btn-holder mobile-only">
          <button class="btn-primary btn-lg" @click="scrollToLastSection">
            Start for Free
          </button>
        </div>
      </div>
    </div>

    <div v-if="$config.platform === 'fa'" class="section testimonials-simple">
      <div class="container">
        <h2 class="section-title">
          Connect With Thousands of <span class="highlight yellow thin">Satisfied Learners</span> Worldwide
        </h2>
        <img :src="require(`../../assets/images/fa-landing-testimonials${$store.state.layout === 'mobile' ? '-mobile' : ''}.webp`)" alt="Connect With Thousands of Satisfied Learners Worldwide">
      </div>
    </div>

    <StartLearningToday
      id="start-learn-today" class="start-learn-today prefooter" class-name="start-your-journey-for-free-v2 prefooter"
      unique-id="prefooter"
      event-prefix="start_your_journey_v2_footer_"
    >
      <h2 class="section-title">
        <span>
          Try <component :is="$config.platform === 'ds' ? 'LogoExtended' : 'LogoFaExtended'" :class="$config.platform" :title="$config.platformName" /><br>for <span class="highlight yellow">FREE</span>
        </span>
      </h2>
      <ul>
        <li class="body-1">
          First 2 course sections free
        </li>
        <li class="body-1">
          {{ stats.rounded.exercises }}+ hands-on exercises
        </li>
        <li class="body-1">
          Real-world {{ $config.platform === 'ds' ? 'data' : 'finance' }} projects
        </li>
        <li class="body-1">
          Downloadable materials
        </li>
        <li class="body-1">
          Practice exams
        </li>
        <li class="body-1">
          Resume builder
        </li>
        <li class="body-1">
          Gamified learning experience
        </li>
        <li class="body-1">
          Community forum
        </li>
      </ul>
    </StartLearningToday>
  </div>
</template>

<script>
import Rating from '@itbpbg/lms-components/src/Courses/Rating.vue';
import Companies from '@itbpbg/lms-components/src/Campaigns/Companies.vue';
import Technologies from '@itbpbg/lms-components/src/components/Technologies.vue';
import Videos from '@itbpbg/lms-components/src/Campaigns/Videos.vue';
import SliderInfinite from '@itbpbg/lms-components/src/components/SliderInfinite.vue';
import { loadHotJar } from '@itbpbg/lms-components/src/utils/helper';
import StartLearningToday from '../../components/StartLearningToday';
import StatsIcons from '../Campaigns/StatsIcons';
import IconCreditCard from '../../assets/svg/icon-credit-card.svg?vue-component';
import IconTrustpilot from '../../assets/svg/icon-trustpilot.svg?vue-component';
import IconUsers from '../../assets/svg/icon-users-start-your-journey-for-free.svg?vue-component';
import IconThunder from '../../assets/svg/icon-thunder-start-your-journey-for-free.svg?vue-component';
import IconMonitor from '../../assets/svg/icon-monitor-play-start-your-journey-for-free.svg?vue-component';
import IconLayers from '../../assets/svg/icon-layers-start-your-journey-for-free.svg?vue-component';
import Courses from '../Campaigns/Courses.vue';
import LogoExtended from '../../assets/svg/365-logo-extended.svg?vue-component';
import LogoFaExtended from '../../assets/svg/365-fa-logo-extended.svg?vue-component';

export default {
  components: {
    SliderInfinite,
    Videos,
    Courses,
    Companies,
    Technologies,
    Rating,
    StartLearningToday,
    StatsIcons,
    IconTrustpilot,
    IconCreditCard,
    IconUsers,
    IconThunder,
    IconMonitor,
    IconLayers,
    LogoExtended,
    LogoFaExtended,
  },
  props: {
    stats: {
      type: Object,
      default: () => ({}),
    },
    page: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      companiesSmall: [
        { icon: 'oracle' },
        { icon: 'amazon' },
        { icon: 'walmart' },
        { icon: 'alphabet' },
        { icon: 'meta-new' },
      ],
      companiesFa: [
        { icon: 'hsbc-new' },
        { icon: 'morgan-stanley-new' },
        { icon: 'goldman-sachs-new' },
        { icon: 'pwc-new' },
        { icon: 'coca-cola-auto' },
        { icon: 'ikea-new' },
      ],
      technologies: [
        { icon: 'gray-python', text: 'Python' },
        { icon: 'gray-git', text: 'Git and Github' },
        { icon: 'gray-tableau', text: 'Tableau' },
        { icon: 'gray-r' },
        { icon: 'gray-excel', text: 'Excel' },
        { icon: 'gray-jupyter', text: 'Jupyter' },
        { icon: 'gray-sql' },
        { icon: 'gray-chatgpt', text: 'ChatGPT' },
        { icon: 'gray-powerbi', text: 'Power BI' },
      ],
      videoJsLoaded: false,
      videos: [
        {
          name: 'Lee McFarling',
          image: 'lee',
          from: 'USA',
          id: 6315230915112,
          quote: 'After 6 months of learning with 365 Data Science, I was getting job interviews as a business analyst or a data analyst.',
          job: 'Data Analyst, Astreya',
        },
        {
          name: 'Meghan Verschoor',
          image: 'meghan',
          from: 'Canada',
          id: 6315228912112,
          quote: 'I find the platform really easy to follow through with and learn the skills that I needed to apply to my job. I highly recommend it.',
          job: 'Director, Data Science & Management, Turnstone Biologics',
        },
        {
          name: 'Nada Abdulwahab',
          image: 'nada',
          from: 'Egypt',
          id: 6315228795112,
          quote: '365 gave me the basics to start my career […] and still helps me enhance my skills and push my career to a higher level.',
          job: 'Senior Data Engineer, VOIS',
        },
        {
          name: 'Julián Elizondo',
          image: 'julián',
          from: 'Mexico',
          id: 6343656185112,
          quote: 'If it wasn’t for 365 Data Science and the confidence it gave me, I wouldn’t have landed my current job.',
          job: 'Tableau Developer, Schneider Electric',
        },
        {
          name: 'Mihaela Paparinova',
          image: 'mihaela',
          from: 'Bulgaria',
          id: 6341101204112,
          quote: 'The program helped me excel in my studies and deepen my understanding of niche data science concepts.',
          job: 'Data Science Student, Sofia University',
        },
        {
          name: 'Ernest Asena',
          image: 'ernest',
          from: 'Kenya',
          id: 6315231920112,
          quote: 'Eight months into the program, I started applying for entry-level positions, and I landed a job as a junior data scientist.”',
          job: 'Data Insights Analytics Cloud Engineer, AWS',
        },
        {
          name: 'Ahmed Eldessouky',
          image: 'ahmed',
          from: 'Egypt',
          id: 6327293863112,
          quote: 'I was impressed with the quality of the video lessons, and the topics and different tools the program covers.',
          job: 'Business Developer, Squadio',
        },
      ],
      courseGroupIdx: 0,
      salaryAnimation: {
        activate: false,
        animatedOnScroll: false,
      },
      instructors: {
        ds: [
          {
            name: 'Davis Balaba',
            slug: 'davis-balaba',
          },
          {
            name: 'Danielle Thé',
            slug: 'danielle-the',
          },
          {
            name: 'Bernard Marr',
            slug: 'bernard-marr',
          },
          {
            name: 'Anastasia Kuznetsova',
            slug: 'anastasia-kuznetsova',
          },
          {
            name: 'Jeff Lee',
            slug: 'jeff-li-1',
          },
          {
            name: 'Shashank Kalanithi',
            slug: 'shashank-kalanithi-1',
          },
          {
            name: 'Lauren Newbould',
            slug: 'lauren-newbould-1',
          },
          {
            name: 'Olivier Maugain',
            slug: 'olivier-maugain',
          },
          {
            name: 'Randy Rosseel',
            slug: 'randy-rosseel',
          },
          {
            name: 'Tina Huang',
            slug: 'tina-huang',
          },
          {
            name: 'Ken Jee',
            slug: 'ken-jee',
          },
        ],
        fa: [
          {
            name: 'Iliya Valchanov',
            slug: 'iliya-valchanov',
          },
          {
            name: 'Ivan Kitov',
            slug: 'ivan-kitov',
          },
          {
            name: 'Gilbert Eijkelenboom',
            slug: 'gilbert-eijkelenboom',
          },
          {
            name: 'Ned Krastev',
            slug: 'ned-krastev',
          },
          {
            name: 'Randy Rosseel',
            slug: 'randy-rosseel',
          },
          {
            name: 'Antoniya Baltova',
            slug: 'antoniya-baltova',
          },
          {
            name: 'Peter Lipovyanov',
            slug: 'peter-lipovyanov',
          },
          {
            name: 'Valentina Bogdanova',
            slug: 'valentina-bogdanova',
          },
          {
            name: 'Ivan Vaptsarov',
            slug: 'ivan-vaptsarov',
          },
          {
            name: 'Martin Ganchev',
            slug: 'martin-ganchev',
            image: 'martin-ganchev-new',
          },
        ],
      },
    };
  },
  computed: {
    instructorsTitle() {
      if (this.$config.platform === 'fa') {
        return 'Learn Finance from <span class="highlight yellow thin">Experts</span> at High-Profile Companies';
      }

      return 'Learn Data and AI with <span class="highlight blue thin">Instructors</span> from<br><span class="smaller">Meta, Netflix, Google, Spotify, IKEA, etc.</span>';
    },
    platformInstructors() {
      return this.instructors[this.$config.platform] || [];
    },
    coursesGroups() {
      return this.page.coursesGroups || [];
    },
    courseGroup() {
      return this.coursesGroups[this.courseGroupIdx] || {};
    },
    courses() {
      return (this.page.courses || []).filter((c) => this.courseGroup.courses.includes(c.id)) || [];
    },
    salary() {
      const [salary, year] = this.courseGroup.description.earn.split('/');
      const salaryBolded = `<span class="letter bold">${salary.split('').join('</span><span class="letter bold">')}</span>`;
      const yearNormal = `<span class="letter">${year.split('').join('</span><span class="letter">')}</span>`;

      return `${salaryBolded}<span class="letter">/</span>${yearNormal}`;
    },
  },
  async mounted() {
    const eventID = `${this.$store.state.tracking.sessionId}${Date.now()}`;
    dataLayer.push({ event: 'LandingPageView', eventID });
    this.$tracking.sendEvent({
      dataLayerEvent: 'ViewLandingPage',
      destinationUrl: this.$router.currentRoute.fullPath,
      eventID,
      capi: true,
      userAgent: navigator.userAgent,
    });
    await this.$pollValue(() => typeof videojs !== 'undefined');
    this.videoJsLoaded = true;
    this.init();
    loadHotJar();

    window.addEventListener('resize', this.init);
    window.addEventListener('scroll', this.handleScroll);
    this.$once('hook:beforeDestroy', () => window.removeEventListener('scroll', this.handleScroll));
    this.handleScroll();
  },
  methods: {
    init() {

    },
    scrollToLastSection() {
      const signUpToContinue = document.querySelector('.start-learn-today.prefooter');
      signUpToContinue.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    },
    handleScroll() {
      if (process.client) {
        const salary = this.$el.querySelector('.animation-salary');
        if (salary && !this.salaryAnimation.animatedOnScroll) {
          const salaryBounds = salary.getBoundingClientRect();
          if (salaryBounds.top < window.innerHeight * 0.7) {
            this.switchCourseGroup(0, true);
            this.salaryAnimation.animatedOnScroll = true;
          }
        }
      }
    },
    switchCourseGroup(idx, force = false) {
      if (idx === this.courseGroupIdx && !force) {
        return;
      }

      if (this.salaryAnimation.activate) {
        this.salaryAnimation.activate = false;
      }

      let startAnimationAfter = 10;

      if (!force && this.$store.state.layout === 'mobile') {
        const coursesSlider = this.$el.querySelector('.courses-extended .infinite-carousel');
        if (coursesSlider) {
          startAnimationAfter = 500;
          coursesSlider.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      }

      this.salaryAnimation.animatedOnScroll = true;

      setTimeout(() => {
        this.courseGroupIdx = idx;
        this.$nextTick(() => {
          let width = 0;
          const holder = this.$el.querySelector('.animation-salary .holder');
          const letters = holder.querySelectorAll('.letter');
          for (let idx2 = 0; idx2 < letters.length; idx2 += 1) {
            const letter = letters[idx2].getBoundingClientRect();
            width += letter.width;
          }
          this.salaryAnimation.width = width + 2; // add border to calculations
          this.salaryAnimation.activate = true;
          setTimeout(() => {
            for (let idx3 = 0; idx3 < letters.length; idx3 += 1) {
              const letter = letters[idx3];
              setTimeout(() => {
                letter.classList.add('show');
              }, idx3 * 100);
            }
          }, 200);
        });
      }, startAnimationAfter);
    },
    subscribe() {
      const plan = this.$store.getters['subscriptions/annual'];
      this.$store.dispatch('dataLayer/addEvent', { event: 'addToCart', value: { ...plan, planTitle: plan.title, currency: plan.currency.value } });

      return this.$router.push({
        path: '/checkout/',
        query: { plan: 'annual', coupon: this.page.coupon },
      });
    },
  },
};
</script>

<style lang="scss">
.start-for-free-page-v2 {
  .muted {
    color: var(--main-light-shade-4);
  }

  .start-learn-today {
    .signup-form-wrapper {
      margin-top: 12px;

      .form-wrap {
        border-radius: 0;
      }
    }
  }

  @include bp($bp-desktop) {
    .mobile-only,
    .laptop-only {
      display: none;
    }

    &.ds {
      .start-learn-today {
        &.v2-head {
          background-image: url('../../assets/images/ds-signup-bg-v2.webp');
        }

        &.prefooter {
          background-image: url('../../assets/images/ds-signup-bg-v2-prefooter.webp');
        }
      }
    }

    &.fa {
      .start-learn-today {
        &.v2-head {
          background-image: url('../../assets/images/fa-signup-bg-v2.webp');
        }

        &.prefooter {
          background-image: url('../../assets/images/fa-signup-bg-v2-prefooter.webp');
        }

        .form-wrap.start-your-journey-for-free-v2 {
          box-shadow: 0px 0px 60px 0px #036FFD;
        }
      }
    }
  }

  .content {
    .heading {
      display: flex;
      padding: var(--spacing-24);
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
      gap: var(--spacing-24);
      border: 4px solid var(--extreme-yellow);
      margin-bottom: var(--spacing-36);

      .first,
      .second {
        font-weight: 500;

        span {
          display: inline-block;
        }
      }

      .first {
        font-size: 44px;
        line-height: 31px;

        span {
          font-weight: 800;
        }
      }

      .second {
        font-size: 32px;
        line-height: 35px;
        white-space: nowrap;

        .highlight {
          font-size: 50px;
          font-weight: 900;
        }

        .laptop-only + span {
          margin-top: 16px;
        }
      }

      @include bp($bp-mobile) {
        text-align: center;
        padding: 12px;
        gap: 12px;

        .first {
          font-size: 40px;
          line-height: 42px;
        }

        .second {
          font-size: 28px;
          white-space: normal;

          .highlight {
            font-size: 44px;
          }
        }
      }
    }

    ul {
      list-style-type: none;
      margin-bottom: var(--spacing-36);
      padding-left: 0;

      li {
        margin-bottom: var(--spacing-sm);
        padding-left: var(--spacing-30);
        line-height: 20px;
        background-image: url('../../assets/svg/checkmark-start-your-journey-for-free-v2.svg');
        image-rendering: -webkit-optimize-contrast;
        background-repeat: no-repeat;
        background-position-x: 0;
        background-position-y: 0;

        &:before {
          display: none;
        }
      }
    }

    .footer {
      span,
      svg {
        vertical-align: middle;
        display: inline-block;
      }

      svg {
        margin-right: var(--spacing-16);
      }

      @include bp($bp-desktop) {
        span {
          font-size: 18px;
        }
      }
    }
  }

  .section {
    padding: var(--spacing-80) 0;
    background: var(--main-dark);
    color: var(--main-light);
  }

  .btn-holder {
    .btn-primary {
      color: var(--main-dark);
      border: 2px solid var(--extreme-yellow);
      background-color: var(--extreme-yellow);
      font-weight: 600;
      font-size: 21px;
      border-radius: 5px;
      margin: 0 auto;
      display: block;
      height: 58px;
      line-height: 18px;
      max-width: 350px;
      width: 100%;
      padding-left: 5px;
      padding-right: 5px;

      &.submit {
        transition: all 50ms linear;
        border-color: var(--main-dark);
        background-color: var(--extreme-yellow);
        margin-top: 20px;
        margin-bottom: 12px;
        line-height: 30px;
      }

      &:not(:disabled) {
        &:hover,
        &:active,
        &:focus {
          color: var(--main-dark);
          background-color: var(--extreme-yellow);

          &:not(.submit) {
            filter: drop-shadow(0 0 30px var(--extreme-yellow));
            border-color: var(--extreme-yellow);
          }

          &.submit {
            background-color: var(--extreme-yellow);
            border-color: var(--main-dark);
            outline: 2px solid var(--main-dark) !important;
          }
        }
      }
    }
  }

  .highlight {
    &:not(.thin) {
      font-weight: 900;
    }

    &.blue {
      color: var(--extreme-blue);

      &.shadow {
        text-shadow: 0 0 7px var(--extreme-blue);
      }
    }

    &.yellow {
      color: var(--extreme-yellow);

      &.shadow {
        text-shadow: 0 0 7px var(--extreme-yellow);
      }
    }

    &.white {
      color: white;
    }
  }

  &.fa {
    .highlight {
      &.blue {
        color: #68A9FE;

        &.shadow {
          text-shadow: 0 0 7px #036FFD;
        }
      }
    }

    .content ul li {
      background-image: url('../../assets/svg/checkmark-start-your-journey-for-free-v2-fa.svg');
    }
  }

  .trustpilot-section {
    padding: var(--spacing-40) 0;
    background: var(--main-dark-light-1);

    .trustpilot-review {
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 0 var(--spacing-28);

      .icon {
        height: 40px;
        margin-right: var(--spacing-26);
      }

      .course-rating {
        font-size: 28px;
        font-weight: 500;

        .rating {
          font-size: 28px;
          font-weight: 500;
          color: var(--main-light-shade-4);
          margin-right: 20px;
        }

        .rating-stars {
          height: auto;

          svg {
            width: 38px;
            height: 38px;
          }
        }
      }
    }

    .trusted-companies {
      padding-top: 0;
      border: none;

      .title {
        font-weight: 500;
        margin-bottom: 23px;
      }

      .logos {
        opacity: 0.7;

        .logo {
          svg {
            height: 37px;
            margin: 0;

            &.amazon {
              height: 28px;
            }

            &.meta-new {
              height: 26px;
            }

            &,
            g[fill] {
              color: var(--main-light-shade-3);
            }

            path[fill],
            g[fill] {
              fill: var(--main-light-shade-3);
            }
          }
        }
      }
    }
  }

  .courses-extended {
    .head {
      text-align: center;

      h2 {
        margin: 0;
      }

      h3 {
        margin: 40px 0;
      }

      ul {
        list-style: none;
        display: flex;
        justify-content: center;
        flex-wrap: wrap;
        gap: 16px;
        padding-left: 0;

        li {
          padding: 20px;
          font-size: 18px;
          line-height: 1.1;
          color: var(--main-light);
          background-color: var(--main-dark-light-1);
          border: 2px solid transparent;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.2s ease;

          &:hover,
          &.active {
            border-color: var(--accent);
            background-color: var(--main-dark-light-2);
          }
        }
      }

      .description {
        font-size: 24px;
        font-weight: 500;
        margin-top: 60px;
        margin-bottom: 0;

        .static-salary-container {
          width: 188px;
          display: inline-block;

          .animation-salary {
            display: inline-block;
            vertical-align: middle;
            width: 188px;

            .holder {
              display: block;
              white-space: nowrap;
              overflow: hidden;
              width: 0;

              &.animation {
                font-size: 24px;
                font-weight: 600;
                font-family: Source Code Pro, monospace;
                height: 32px;
                text-align: left;
                line-height: 30px;
                color: var(--main-dark);
                border: 1px solid var(--main-light-shade-4);
                border-radius: 4px;
                background: var(--main-light-shade-2);
              }

              &.active {
                transition: width 0.5s;
                width: var(--width, 188px);
              }
            }

            .letter {
              font-weight: normal;
              opacity: 0;
              transition: all 0.1s linear;

              &.show {
                opacity: 1;
              }

              &:first-child {
                padding-left: 4px;
              }

              &:last-child {
                padding-right: 4px;
              }

              &.bold {
                font-weight: 700;
              }
            }
          }
        }
      }
    }
  }

  .infinite-carousel {
    .controls {
      margin-top: 0;
      margin-bottom: var(--spacing-32);
    }

    .carousel {
      padding-top: 10px;
      margin-top: -10px;
      padding-bottom: 10px;
      margin-bottom: -10px;

      .card {
        margin: 0 var(--spacing-30) 0 0;
        width: 370px;
        vertical-align: top;

        .course-card-container {
          min-height: 422px;
        }

        &:not(.course-card) {
          flex-direction: column;
          align-items: flex-start;
          height: auto;
          border-radius: 0;

          .image-wrapper img {
            display: block;
            max-width: 100%;
            max-height: 100%;
            border-radius: 7px;
          }

          .card-title {
            margin-top: var(--spacing-20);
            font-size: 32px;
            color: var(--main-light);
          }

          .card-desc {
            color: var(--main-dark-shade-4);
            font-size: 21px;
            line-height: 140%;
          }
        }

        &.course-card {
          .course-card-image {
            height: 220px;
          }

          .course-card-body {
            padding: var(--spacing-20) var(--spacing-24) 20px;

            .course-name {
              margin-bottom: var(--spacing-8);
            }

            .course-rating {
              color: var(--main-light);
            }

            .info {
              color: var(--main-light);
              margin: 0;

              .dot {
                background: var(--main-light);
              }
            }
          }
        }
      }
    }
  }

  .info-section-wrapper {
    padding: var(--spacing-40) 0;
    background: var(--main-dark-light-1);
    color: var(--main-light);

    .info-section {
      display: flex;
      gap: var(--spacing-40);

      div {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
        text-align: center;

        p {
          line-height: 150%;
          margin: 0;

          &:first-of-type {
            font-size: 18px;
            font-weight: 600;
          }
        }

        svg {
          margin-bottom: var(--spacing-12);

          &:first-of-type {
            margin-bottom: var(--spacing-8);
          }
        }
      }
    }
  }

  .graduations {
    .slider-header {
      margin-bottom: var(--spacing-32);

      .heading {
        margin: 0 0 var(--spacing-60);

        .title-highlight {
          margin: 0;
        }
      }
    }

    .btn-holder {
      margin-top: var(--spacing-60);
    }
  }

  .instructors {
    h2 .smaller {
      font-size: 32px;
    }

    .infinite-head .h4 {
      text-align: center;
      font-size: 40px;
      margin-bottom: var(--spacing-32);

      .smaller {
        font-size: 32px;
      }
    }

    .trusted-companies {
      padding-top: 0;
      margin-bottom: 24px;
      border-top: none;

      .logos {
        justify-content: center;

        .logo svg {
          height: 26px;
          margin: 0 24px;
          color: white;
        }
      }
    }

    .stats-icons.colored svg {
      color: #68A9FE;
      width: 60px;
    }

    .instructors-slider {
      .heading {
        text-align: center;
      }

      .infinite-carousel .carousel .inner > * {
        width: 210px;
        margin-right: 30px;

        img {
          display: block;
          border-radius: 20px;
          max-width: 100%;
          width: 100%;
        }
      }
    }
  }

  .master {
    background: #1A2646;

    .muted {
      font-size: 21px;
      margin: 16px 0 0;
    }

    img {
      display: block;
      max-width: 100%;
    }

    .btn-primary {
      max-width: 270px;
      width: 100%;
      margin-left: 0;
    }
  }

  .testimonials-simple {
    text-align: center;

    img {
      display: block;
      max-width: 100%;
      margin-left: auto;
      margin-right: auto;
    }
  }

  .prefooter {
    .content {
      h2 {
        margin: 0 0 var(--spacing-48);

        span {
          svg {
            height: 43px;

            .fa {
              height: 36px;
              vertical-align: middle;
            }
          }
        }
      }

      ul {
        margin-bottom: 0;
      }
    }
  }

  @include bp($bp-mobile) {
    .section {
      padding: 60px 0;
    }

    .section-title,
    .instructors h2 {
      font-size: 32px;

      .smaller {
        font-size: 28px;
      }
    }

    .courses-extended {
      .head ul {
        justify-content: flex-start;
      }
    }

    .infinite-carousel .carousel .card {
      width: 327px;
    }

    .trustpilot-section {
      padding-left: 0;
      padding-right: 0;

      .trustpilot-review {
        flex-wrap: wrap;

        .icon {
          margin-bottom: var(--spacing-16);
        }
      }

      .course-rating {
        height: auto;
      }

      .trusted-companies {
        margin-bottom: 0;
      }
    }

    .info-section-wrapper .info-section {
      display: block;

      div + div {
        margin-top: var(--spacing-40);
      }
    }

    .technologies-wrapper .technologies {
      flex-wrap: wrap;
      column-gap: 48px;
      row-gap: 32px;
      margin-bottom: 0;
      justify-content: space-evenly;
    }

    .instructors {
      .infinite-head .h4 {
        font-size: 32px;

        .smaller {
          font-size: 28px;
        }
      }

      .instructors-slider .infinite-carousel .carousel .inner > * {
        max-width: 327px;
        width: 100%;
      }
    }

    .master {
      text-align: center;

      .btn-primary {
        margin-left: auto;
      }

      img {
        margin: 40px 0 60px;
      }
    }

    .prefooter .content h2 {
      text-align: center;

      span {
        svg {
          max-height: 40px;
          max-width: 96%;
        }
      }
    }
  }

  @include bp($bp-laptop) {
    .mobile-only {
      display: none;
    }

    .laptop-only {
      display: block;
    }

    &.ds {
      .start-learn-today {
        &.v2-head {
          background-image: url('../../assets/images/ds-signup-bg-v2-laptop.webp');
        }

        &.prefooter {
          background-image: url('../../assets/images/ds-signup-bg-v2-prefooter-laptop.webp');
        }
      }
    }

    &.fa {
      .start-learn-today {
        &.v2-head {
          background-image: url('../../assets/images/fa-signup-bg-v2-laptop.webp');
        }

        &.prefooter {
          background-image: url('../../assets/images/fa-signup-bg-v2-prefooter-laptop.webp');
        }
      }
    }

    .content .heading {
      .first {
        font-size: 40px;
      }

      .second {
        font-size: 28px;

        .highlight {
          font-size: 44px;
        }
      }
    }

    .infinite-carousel .carousel .card,
    .btn-holder .btn-primary {
      width: 310px;
    }

    .instructors {
      h2 .smaller {
        font-size: 28px;
      }

      .instructors-slider .infinite-carousel .carousel .inner > * {
        width: 178px;
      }
    }

    .prefooter .content h2 {
      font-size: 40px;

      span svg {
        height: 39px;
      }
    }
  }

  @include bp($bp-mobile) {
    .desktop-only,
    .laptop-only {
      display: none !important;
    }

    &.ds {
      .start-learn-today {
        &.v2-head {
          background-image: url('../../assets/images/ds-signup-bg-v2-mobile-new.webp');
        }

        &.prefooter {
          background-image: url('../../assets/images/ds-signup-bg-v2-prefooter-mobile-new.webp');
        }
      }
    }

    &.fa {
      .start-learn-today {
        &.v2-head {
          background-image: url('../../assets/images/fa-signup-bg-v2-mobile-new.webp');
        }

        &.prefooter {
          background-image: url('../../assets/images/fa-signup-bg-v2-prefooter-mobile-new.webp');
        }
      }
    }

    .content .heading {
      background: rgba(0, 12, 31, 0.50);
    }

    .btn-holder {
      .btn-primary {
        max-width: 327px;
        width: 100%;
      }
    }
  }
}

//.fa {
//  @import '../../assets/sass/fa/pages/start-your-journey-for-free.scss';
//}
</style>
